import React, { useState, useRef, useEffect } from 'react';
import { askSCG } from '../services/api';
import SCGRuleLink from '../components/SCGRuleLink';

const SCG_RULE_PATTERN = /SCG-[A-Za-z0-9-]+/g;

/**
 * Parse answer text and replace SCG rule ID patterns with clickable SCGRuleLink components.
 */
function renderAnswerWithLinks(text) {
  if (!text) return null;
  const parts = [];
  let lastIndex = 0;
  let match;
  const regex = new RegExp(SCG_RULE_PATTERN.source, 'g');

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <SCGRuleLink key={`${match[0]}-${match.index}`} ruleId={match[0]} />
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

export default function AskSCG() {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const question = inputText.trim();
    if (!question || loading) return;

    const userMsg = { role: 'user', content: question };
    setMessages((prev) => [...prev, userMsg]);
    setInputText('');
    setLoading(true);

    try {
      const data = await askSCG(question);
      const assistantMsg = {
        role: 'assistant',
        content: data.answer,
        cited_rules: data.cited_rules || [],
        retrieval_time_ms: data.retrieval_time_ms,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      const errorMsg = {
        role: 'assistant',
        content: `Error: ${err?.response?.data?.detail || err.message || 'Request failed'}`,
        cited_rules: [],
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const levelColor = (level) => {
    if (!level) return 'var(--text-muted)';
    const l = level.toUpperCase();
    if (l.includes('TOP SECRET')) return '#ef4444';
    if (l.includes('SECRET')) return '#f59e0b';
    if (l.includes('CONFIDENTIAL')) return '#3b82f6';
    return '#22c55e';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '16px', flexShrink: 0 }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
          Ask the SCG
        </h1>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
          Ask natural-language questions about Security Classification Guide rules. Answers cite specific SCG rules — click any rule ID for details.
        </p>
      </div>

      {/* Messages area */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {messages.length === 0 && !loading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', maxWidth: '420px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>?</div>
              <p style={{ fontSize: '15px', marginBottom: '8px' }}>No messages yet</p>
              <p style={{ fontSize: '13px', opacity: 0.7 }}>
                Try asking something like "What rules cover SIGINT sources?" or "When should I mark something NOFORN?"
              </p>
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <div
              style={{
                maxWidth: '75%',
                padding: '12px 16px',
                borderRadius: '12px',
                fontSize: '14px',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                ...(msg.role === 'user'
                  ? {
                      backgroundColor: 'var(--spotlight-gold, #c9a227)',
                      color: '#1a1a2e',
                      borderBottomRightRadius: '4px',
                    }
                  : {
                      backgroundColor: 'var(--vault-elevated, #252538)',
                      color: 'var(--text-primary, #e0e0e0)',
                      border: '1px solid var(--vault-border, #3a3a4a)',
                      borderBottomLeftRadius: '4px',
                    }),
              }}
            >
              {msg.role === 'user' ? msg.content : renderAnswerWithLinks(msg.content)}

              {/* Cited rules metadata for assistant messages */}
              {msg.role === 'assistant' && msg.cited_rules && msg.cited_rules.length > 0 && (
                <div
                  style={{
                    marginTop: '12px',
                    paddingTop: '10px',
                    borderTop: '1px solid var(--vault-border, #3a3a4a)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                    <span>{msg.cited_rules.length} rule{msg.cited_rules.length !== 1 ? 's' : ''} cited</span>
                    {msg.retrieval_time_ms != null && (
                      <span>{Math.round(msg.retrieval_time_ms)}ms retrieval</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {msg.cited_rules.map((rule, rIdx) => (
                      <span
                        key={rIdx}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontFamily: 'monospace',
                          backgroundColor: 'var(--vault-surface, #1e1e2e)',
                          border: '1px solid var(--vault-border, #3a3a4a)',
                          color: 'var(--text-secondary, #ccc)',
                        }}
                      >
                        <SCGRuleLink ruleId={rule.rule_id} />
                        {rule.classification_level && (
                          <span
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              backgroundColor: levelColor(rule.classification_level),
                              flexShrink: 0,
                            }}
                            title={rule.classification_level}
                          />
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div
              style={{
                padding: '12px 16px',
                borderRadius: '12px',
                borderBottomLeftRadius: '4px',
                backgroundColor: 'var(--vault-elevated, #252538)',
                border: '1px solid var(--vault-border, #3a3a4a)',
                color: 'var(--text-muted)',
                fontSize: '14px',
              }}
            >
              <span className="animate-pulse">Searching SCG rules...</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input bar */}
      <form
        onSubmit={handleSubmit}
        style={{
          flexShrink: 0,
          display: 'flex',
          gap: '8px',
          padding: '12px 0',
          borderTop: '1px solid var(--vault-border, #3a3a4a)',
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question about SCG rules..."
          disabled={loading}
          style={{
            flex: 1,
            padding: '10px 14px',
            borderRadius: '8px',
            border: '1px solid var(--vault-border, #3a3a4a)',
            backgroundColor: 'var(--vault-elevated, #252538)',
            color: 'var(--text-primary, #e0e0e0)',
            fontSize: '14px',
            outline: 'none',
          }}
        />
        <button
          type="submit"
          disabled={loading || !inputText.trim()}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: loading || !inputText.trim() ? 'var(--vault-border, #3a3a4a)' : 'var(--spotlight-gold, #c9a227)',
            color: loading || !inputText.trim() ? 'var(--text-muted)' : '#1a1a2e',
            fontSize: '14px',
            fontWeight: 600,
            cursor: loading || !inputText.trim() ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.15s',
          }}
        >
          {loading ? 'Asking...' : 'Send'}
        </button>
      </form>
    </div>
  );
}
